import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange, computed } from '@angular/core';
import {SureFlowLoopService} from '../../services/sure-flow-loop.service';
import {SureFlowUnitService} from '../../services/sure-flow-unit.service';
import { Router } from '@angular/router';
import {SureFlowRatingService} from '../../services/sure-flow-rating.service';
import {StringUtils} from '../../utils/string-utils';
import {EntityService} from '../../services/entity-service';
import {ScheduleService} from '../../services/schedule.service';
import {Entity} from '../../entity/entity';
import {Observable, Subject} from 'rxjs';
import {EntitySureFlowLoop} from '../../model/entity-sure-flow-loop';
import {SureFlowLoopDetail} from '../../model/sure-flow-loop-detail';
import {EntitySureFlowUnit} from '../../model/entity-sure-flow-unit';
import {WorkingSureFlowUnit} from '../../model/working-sure-flow-unit';
import {SureFlowAutoSelectInput} from '../../model/sure-flow-auto-select-input';
import {SureFlowLoopInitial} from '../../model/sure-flow-loop-initial';
import {SureFlowEntityRatingInput} from '../../model/sure-flow-entity-rating-input';
import {SureFlowEntityUnitRatingInput} from '../../model/sure-flow-entity-unit-rating-input';
import {SureFlowConfigurationService} from '../../services/sure-flow-configuration.service';
import {SureFlowUnitData} from '../../model/sure-flow-unit-data';
import {DialogService} from '../../services/dialog.service';
import {SureFlowUnitConversionService} from '../../services/sure-flow-unit-conversion.service';
import {OrganizationService} from '../../services/organization-service';
import {UserStateService} from '../../services/user-state.service';
import {SureflowUnitUtils} from '../../utils/sureflow-unit-utils';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {SureflowLoopDesignerComponent} from '../sureflow-loop-designer/sureflow-loop-designer.component';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';


@Component({
    selector: 'app-entity-sure-flow-loops',
    templateUrl: './entity-sure-flow-loops.component.html',
    styleUrls: ['./entity-sure-flow-loops.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, SureflowLoopDesignerComponent, MatProgressSpinner]
})
export class EntitySureFlowLoopsComponent implements OnChanges, OnInit {
  private readonly service = inject(SureFlowLoopService);
  private readonly unitService = inject(SureFlowUnitService);
  private readonly router = inject(Router);
  private readonly sureFlowRatingService = inject(SureFlowRatingService);
  private readonly scheduleService = inject(ScheduleService);
  private readonly entityService = inject(EntityService);
  private readonly configurationService = inject(SureFlowConfigurationService);
  private readonly dialogService = inject(DialogService);
  private readonly sureFlowConversionService = inject(SureFlowUnitConversionService);
  private readonly organizationService = inject(OrganizationService);
  private readonly userStateService = inject(UserStateService);
  readonly entityId = input.required<string>();
  readonly entityName = input.required<string>();
  readonly entityRevisionParam = input.required<string>({ alias: 'entityRevision' });
  readonly loops = signal<EntitySureFlowLoop[]>(undefined!);
  readonly loopDetails = signal<SureFlowLoopDetail[]>(undefined!);
  readonly loopUnits = signal<EntitySureFlowUnit[]>(undefined!);
  readonly selectedLoopUnits = signal<WorkingSureFlowUnit[]>(undefined!);
  readonly invalidUnitIds = signal<number[]>(undefined!);
  readonly selectedUnitIds = signal<number[]>([]);
  readonly selectedLoopId = signal<number>(undefined!);
  readonly selectedEntityLoopId = signal<number>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly entity = signal<Entity>(undefined!);
  private readonly entityRevision = computed(() => +this.entityRevisionParam());

  private isUnknownEntity(entityName: string): boolean {
    if (StringUtils.isBlank(entityName)) {
      return true;
    }
    const knownEntities: string[] = ['schedules', 'quotes', 'orders'];
    return knownEntities.indexOf(entityName) < 0;
  }

  ngOnInit(): void {
    this.initialize();
    this.isBusy.set(true);
    this.loadEntity().subscribe({
      next: (entity) => {
        this.entity.set(entity);
        this.organizationService.getOrganization(entity.repOfficeId).subscribe({
          next: (organization) => {
            this.entity().prnField1 = organization.prnCode;
            this.entity().priceListId = organization.priceListId;
            this.loadLoopList().subscribe({
              next: () => this.isBusy.set(false),
              error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private loadLoopList(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    if (StringUtils.isNotBlank(this.entity()?.id?.entityId)) {
      this.isBusy.set(true);
      this.service.getEntityLoops(this.entity().id.entityId, this.entity().id.entityRevision).subscribe({
        next: (loops) => {
          this.loops.set(loops);
          this.loopDetails.set(this.loops().map(loop => loop.detail));
          const selectedLoopId = this.configurationService.getSelectedEntityLoop()?.id;
          if (!StringUtils.isEmptyNumericValue(selectedLoopId)) {
            this.configurationService.clearData();
            const filtered = loops.filter(loop => loop.id === selectedLoopId);
            if (filtered.length === 1) {
              const filteredLoop: EntitySureFlowLoop = filtered[0];
              this.loadLoopData(filteredLoop.id, filteredLoop.detail.id).subscribe({
                next: () => {
                  this.isBusy.set(false);
                  subject.next();
                }, error: (error) => subject.error(error),
                complete: () => subject.complete()
              });
            } else {
              this.isBusy.set(false);
              subject.next();
              subject.complete();
            }
          } else {
            this.isBusy.set(false);
            subject.next();
            subject.complete();
          }
        }, error: (error) => subject.error(error),
        complete: () => subject.complete()
      });
    } else {
      subject.next();
      subject.complete();
    }
    return subject.asObservable();
  }

  private loadEntity(): Observable<Entity> {
    const subject = new Subject<Entity>();
    if (StringUtils.isNotBlank(this.entityId())) {
      if (StringUtils.equalsIgnoreCase('schedules', this.entityName())) {
        this.scheduleService.getSchedule(this.entityId()).subscribe({
          next: (schedule) => subject.next(schedule),
          error: (error) => subject.error(error),
          complete: () => subject.complete()
        });
      } else {
        const entityName = this.getEntityName();
        this.entityService.getEntity(entityName, this.entityId(), this.entityRevision()).subscribe({
          next: (entity) => subject.next(entity),
          error: (error) => subject.error(error),
          complete: () => subject.complete()
        });
      }
    } else {
      subject.error('Cannot load an entity without an entity id');
    }
    return subject.asObservable();
  }

  private getEntityName(): string {
    return this.getDisplayEntityName().toUpperCase();
  }

  getDisplayEntityName(): string {
    switch (this.entityName()) {
      case 'schedules':
        return 'Schedule';
      case 'quotes':
        return 'Quote';
      case 'orders':
        return 'Contract';
      default:
        return '';
    }
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.formatErrorMessage(error));
    this.isBusy.set(false);
  }

  backToEntity(): void {
    if (StringUtils.isBlank(this.entityId())) {
      this.router.navigate(['main']);
    } else {
      const entityName: string = this.getEntityName();
      if (StringUtils.isBlank(entityName)) {
        this.router.navigate(['main']);
      } else if ('SCHEDULE' === entityName) {
        this.router.navigate(['schedules', this.entityId()]);
      } else {
        this.router.navigate([this.entityName(), this.entityId(), this.entityRevision()]);
      }
    }
  }

  changeSelectedLoopId(detail: SureFlowLoopDetail): void {
    this.userStateService.clearLoopDeltaValues();
    const entityLoop: EntitySureFlowLoop = this.getEntityLoop(detail.id);
    this.selectedLoopId.set(detail?.id || 0);
    this.loadLoopData(entityLoop?.id, detail?.id).subscribe({
      error: (error) => this.handleError(error)
    });
  }

  private loadLoopData(loopId: number, detailId: number): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    this.changeEntityLoopId(loopId || 0, detailId || 0).subscribe({
      next: () => {
        this.selectedLoopId.set(detailId || 0);
        subject.next();
      }, error: (error) => subject.error(error),
      complete: () => subject.complete()
    });
    return subject.asObservable();
  }

  private getEntityLoop(detailId: number): EntitySureFlowLoop {
    const filtered = this.loops().filter(loop => loop.detail.id === detailId);
    if (filtered.length === 1) {
      return JSON.parse(JSON.stringify(filtered[0]));
    }
    return undefined!;
  }

  private changeEntityLoopId(newLoopId: number, newDetailId: number): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    this.isBusy.set(true);
    this.selectedLoopId.set(newDetailId);
    this.selectedEntityLoopId.set(newLoopId);
    if (newLoopId > 0) {
      this.unitService.getEntityLoopUnits(newLoopId).subscribe({
        next: (units) => {
          this.loopUnits.set(units);
          this.invalidUnitIds.set(this.getInvalidUnitIds());
          this.selectedLoopUnits.set(this.convertToWorkingUnits());
          this.isBusy.set(false);
          subject.next();
        }, error: (error) => subject.error(error),
        complete: () => subject.complete()
      });
    } else {
      this.isBusy.set(false);
      subject.next();
      subject.complete();
    }
    return subject.asObservable();
  }

  private convertToWorkingUnits(): WorkingSureFlowUnit[] {
    const converted: WorkingSureFlowUnit[] = [];
    if ((this.loopUnits()?.length || 0) > 0) {
      for (const loopUnit of this.loopUnits()) {
        converted.push(this.convertToWorkingUnit(loopUnit));
      }
    }
    return converted;
  }

  private convertToWorkingUnit(loopUnit: EntitySureFlowUnit): WorkingSureFlowUnit {
    const converted = new WorkingSureFlowUnit();
    converted.unitData = loopUnit.unitData;
    converted.loopId = loopUnit.loopId;
    converted.sequence = loopUnit.sequence;
    converted.id = loopUnit.id;
    return converted;
  }

  autoSelectUnits(input: SureFlowAutoSelectInput): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    const entityLoop: EntitySureFlowLoop = this.getEntityLoop(input.loopDetail.id);
    entityLoop.detail = input.loopDetail;
    input.loopId = entityLoop.id;
    this.sureFlowRatingService.autoSelectEntityUnits(input).subscribe({
      next: () => {
        this.loadEntity().subscribe({
          next: () => {
            this.unitService.getEntityLoopUnits(entityLoop.id).subscribe({
              next: (units) => {
                this.loopUnits.set(units);
                this.invalidUnitIds.set(this.getInvalidUnitIds());
                this.selectedLoopUnits.set(this.convertToWorkingUnits());
                this.loadLoopList().subscribe({
                  next: () => {
                    this.changeEntityLoopId(entityLoop.id, entityLoop.detail.id).subscribe({
                      next: () => this.isBusy.set(false),
                      error: (error) => this.handleError(error)
                    });
                  }, error: (error) => this.handleError(error),
                });
              }, error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  addLoop(input: SureFlowLoopInitial): void {
    if (StringUtils.isNotBlank(input?.systemTypeCode)) {
      this.isBusy.set(true);
      const detail: SureFlowLoopDetail = SureflowUnitUtils.createLoopDetail(input);
      const loop: EntitySureFlowLoop = new EntitySureFlowLoop();
      loop.entityId = this.entity().id.entityId;
      loop.entityRevision = this.entity().id.entityRevision;
      loop.schedule = this.isSchedule();
      loop.detail = detail;
      this.service.saveEntityLoop(loop).subscribe({
        next: (inserted) => {
          this.loadLoopList().subscribe({
            next: () => {
              this.changeEntityLoopId(inserted.id, inserted.detail.id).subscribe({
                next: () => this.isBusy.set(false),
                error: (error) => this.handleError(error)
              });
            }, error: (error) => this.handleError(error),
          });
        }, error: (error) => this.handleError(error)
      });
    }
  }

  private isSchedule(): boolean {
    return StringUtils.equalsIgnoreCase('SCHEDULE', this.getEntityName());
  }

  deleteLoop(input: SureFlowLoopDetail): void {
    const entityLoop: EntitySureFlowLoop = this.getEntityLoop(input.id);
    this.service.deleteEntityLoop(entityLoop.id, this.entity().id.entityId, this.entity().id.entityRevision).subscribe({
      next: () => {
        this.loadLoopList().subscribe({
          next: () => {
            this.changeEntityLoopId(0, 0).subscribe({
              next: () => this.isBusy.set(false),
              error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error),
        });
      }, error: (error) => this.handleError(error),
    });
  }

  copyLoop(detail: SureFlowLoopDetail): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    const entityLoop: EntitySureFlowLoop = this.getEntityLoop(detail.id);
    const ratingInput: SureFlowEntityRatingInput = new SureFlowEntityRatingInput();
    ratingInput.loop = JSON.parse(JSON.stringify(entityLoop));
    this.service.copyEntityLoop(ratingInput).subscribe({
      next: () => {
        this.loadLoopList().subscribe({
          next: () => {
            this.changeEntityLoopId(0, 0).subscribe({
              next: () => this.isBusy.set(false),
              error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error),
        });
      }, error: (error) => this.handleError(error)
    });
  }

  deleteLoopUnits(): void {
    this.isBusy.set(true);
    const unitsToDelete = this.getSelectedUnitDetails();
    if (unitsToDelete.length > 0) {
      const ids: number[] = unitsToDelete.map(unit => unit.id);
      this.unitService.deleteEntityLoopUnits(ids).subscribe({
        next: () => {
          this.loadLoopList().subscribe({
            next: () => {
              this.changeEntityLoopId(this.selectedEntityLoopId(), this.selectedLoopId()).subscribe({
                next: () => {
                  this.selectedUnitIds.set([]);
                  this.isBusy.set(false);
                }, error: (error) => this.handleError(error)
              });
            }, error: (error) => this.handleError(error),
          });
        }, error: (error) => this.handleError(error),
      });
    } else {
      this.isBusy.set(false);
    }
  }

  getSelectedUnitDetails(): WorkingSureFlowUnit[] {
    if ((this.selectedLoopUnits()?.length || 0) > 0 && (this.selectedUnitIds()?.length || 0) > 0) {
      return this.selectedLoopUnits().filter(unit => this.selectedUnitIds().indexOf(unit.id) >= 0);
    }
    return [];
  }

  saveLoopDescription(detail: SureFlowLoopDetail): void {
    const entityLoop: EntitySureFlowLoop = this.getEntityLoop(detail.id);
    entityLoop.detail.description = detail.description;
    this.isBusy.set(true);
    this.service.saveEntityLoop(entityLoop).subscribe({
      next: () => {
        this.loadLoopList().subscribe({
          next: () => {
            this.changeEntityLoopId(entityLoop.id, entityLoop.detail.id).subscribe({
              next: () => this.isBusy.set(false),
              error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error),
        });
      }, error: (error) => this.handleError(error)
    });
  }

  updateAndRecalculateLoop(detail: SureFlowLoopDetail): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    const entityLoop: EntitySureFlowLoop = this.getEntityLoop(detail.id);
    const activeFlags: WorkingSureFlowUnit[] = JSON.parse(JSON.stringify(this.selectedLoopUnits())) as WorkingSureFlowUnit[];
    this.unitService.getEntityLoopUnits(entityLoop.id).subscribe({
      next: (units) => {
        this.loopUnits.set(units);
        this.invalidUnitIds.set(this.getInvalidUnitIds());
        this.selectedLoopUnits.set(this.convertToWorkingUnits());
        entityLoop.detail = detail;
        const input: SureFlowEntityRatingInput = new SureFlowEntityRatingInput();
        input.loop = entityLoop;
        const ratingInputs: SureFlowEntityUnitRatingInput[] = [];
        for (let loopUnit of units) {
          const ratingInput: SureFlowEntityUnitRatingInput = new SureFlowEntityUnitRatingInput();
          const unitData: SureFlowUnitData = activeFlags.filter(workingUnit => workingUnit.unitData.id === loopUnit.unitData.id).map(workingUnit => workingUnit.unitData)[0];
          loopUnit.unitData.active = unitData.active;
          ratingInput.unit = loopUnit;
          ratingInputs.push(ratingInput);
        }
        input.units = ratingInputs;
        this.sureFlowRatingService.updateAndSaveEntityLoopAndUnits(input).subscribe({
          next: () => {
            this.loadLoopList().subscribe({
              next: () => {
                this.changeEntityLoopId(entityLoop.id, detail.id).subscribe({
                  next: () => this.isBusy.set(false),
                  error: (error) => this.handleError(error)
                });
              }, error: (error) => this.handleError(error),
            });
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private getInvalidUnitIds(): number[] {
    const invalidIds: number[] = [];
    if ((this.loopUnits()?.length || 0) > 0 && (this.entity()?.lines?.length || 0) > 0) {
      for (const unit of this.loopUnits()) {
        const entityLineFilter = this.entity().lines.filter(line => line.lineId === unit.entityLineId);
        if (entityLineFilter.length === 1) {
          if (!entityLineFilter[0].configurationComplete) {
            invalidIds.push(unit.id);
          }
        }
      }
    }
    return invalidIds;
  }

  copyLoopUnits(): void {
    this.isBusy.set(true);
    const unitsToCopy = this.getSelectedUnitDetails();
    if (unitsToCopy.length > 0) {
      const entityLoopId = unitsToCopy[0]?.loopId || 0;
      const selectedLoop = this.loops().filter(loop => loop.id === entityLoopId)[0];
      const input: SureFlowEntityRatingInput = new SureFlowEntityRatingInput();
      input.loop = selectedLoop;
      const entityUnits = this.getEntityUnitList(unitsToCopy)
      const ratingInputs: SureFlowEntityUnitRatingInput[] = [];
      for (const entityUnit of entityUnits) {
        const ratingInput: SureFlowEntityUnitRatingInput = new SureFlowEntityUnitRatingInput();
        ratingInput.unit = entityUnit;
        ratingInputs.push(ratingInput);
      }
      input.units = ratingInputs;
      this.unitService.copyEntityUnits(input).subscribe({
        next: () => {
          this.loadLoopList().subscribe({
            next: () => {
              this.changeEntityLoopId(selectedLoop.id, selectedLoop.detail.id).subscribe({
                next: () => this.isBusy.set(false),
                error: (error) => this.handleError(error)
              });
            }, error: (error) => this.handleError(error),
          });
        }, error: (error) => this.handleError(error),
      });
    } else {
      this.isBusy.set(false);
    }
  }

  addUnitToLoop(detail: SureFlowLoopDetail) {
    if (this.entity()) {
      const entityLoop: EntitySureFlowLoop = this.getEntityLoop(detail.id);
      this.configurationService.clearData();
      this.configurationService.setSelectedEntity(this.entity());
      this.configurationService.setSelectedEntityLoop(entityLoop);
      this.configurationService.setEntityName(this.entityName());
      this.router.navigate(['sureflow', 'unit', 'configure']);
    }
  }

  editUnits(): void {
    const units = this.getSelectedUnitDetails();
    if ((units?.length || 0) > 0) {
      const entityLoops: EntitySureFlowLoop[] = this.loops().filter(loop => loop.id === units[0].loopId);
      if (entityLoops.length === 1) {
        const entityLoop: EntitySureFlowLoop = entityLoops[0];
        const entityUnits: EntitySureFlowUnit[] = this.getEntityUnitList(units);
        const nonRatedUnits: EntitySureFlowUnit[] = entityUnits.filter(unit => (unit.unitData?.sureFlowRatingUnit?.id || 0) === 0);
        if (entityUnits.length === 1 && nonRatedUnits.length === 1) {
          this.updateNonRatedUnits(nonRatedUnits, entityLoop, entityUnits, true);
        } else if (nonRatedUnits.length > 0) {
          this.updateNonRatedUnits(nonRatedUnits, entityLoop, entityUnits, false);
        } else {
          this.configurationService.clearData();
          this.configurationService.setSelectedEntity(this.entity());
          this.configurationService.setSelectedEntityLoop(entityLoop);
          this.configurationService.setEntityName(this.entityName());
          this.configurationService.setSelectedEntityUnits(entityUnits.filter(unit => (unit.unitData?.sureFlowRatingUnit?.id || 0) > 0));
          this.router.navigate(['sureflow', 'unit', 'configure']);
        }
      }
    }
  }

  private updateNonRatedUnits(nonRatedUnits: EntitySureFlowUnit[], entityLoop: EntitySureFlowLoop, selectedUnits: EntitySureFlowUnit[], showTags: boolean): void {
    const unitData = JSON.parse(JSON.stringify(nonRatedUnits[0].unitData));
    this.dialogService.showAddNonSureFlowUnitDialog(entityLoop.detail.systemTypeCode, unitData, showTags).subscribe({
      next: (input: SureFlowUnitData) => {
        if (input) {
          this.isBusy.set(true);
          this.errorMessage.set(undefined!);
          const ratingInput = new SureFlowEntityRatingInput();
          ratingInput.loop = entityLoop;
          ratingInput.units = [];
          for (const selectedUnit of selectedUnits) {
            const unitRatingInput = new SureFlowEntityUnitRatingInput();
            selectedUnit.unitData.coolingTargetTotalLoad = input.coolingTargetTotalLoad;
            selectedUnit.unitData.heatingTargetTotalLoad = input.heatingTargetTotalLoad;
            if ((selectedUnit.unitData?.sureFlowRatingUnit?.id || 0) === 0) {
              selectedUnit.unitData.coolingFluidFlowRate = input.coolingFluidFlowRate;
              selectedUnit.unitData.heatingFluidFlowRate = input.heatingFluidFlowRate;
              if (showTags) {
                selectedUnit.unitData.userTag = input.userTag;
              }
            }
            unitRatingInput.unit = selectedUnit;
            ratingInput.units.push(unitRatingInput);
          }
          this.unitService.saveEntityLoopUnits(ratingInput).subscribe({
            next: () => {
              this.loadLoopList().subscribe({
                next: () => {
                  this.changeEntityLoopId(entityLoop.id, entityLoop.detail.id).subscribe({
                    next: () => this.isBusy.set(false),
                    error: (error) => this.handleError(error)
                  });
                }, error: (error) => this.handleError(error),
              });
            }, error: (error) => this.handleError(error),
          });
        }
      }, error: (error) => this.handleError(error),
    });
  }

  private getEntityUnitList(workingUnits: WorkingSureFlowUnit[]): EntitySureFlowUnit[] {
    const entityUnits: EntitySureFlowUnit[] = [];
    for (const workingUnit of workingUnits) {
      const filtered = this.loopUnits().filter(unit => unit.id === workingUnit.id);
      if (filtered.length === 1) {
        entityUnits.push(filtered[0]);
      }
    }
    return entityUnits;
  }

  addNonSureFlowUnit(detail: SureFlowLoopDetail): void {
    const entityLoop: EntitySureFlowLoop = this.getEntityLoop(detail.id);
    const unitData: SureFlowUnitData = new SureFlowUnitData();
    unitData.active = true;
    unitData.userTag = 'External Unit';
    this.dialogService.showAddNonSureFlowUnitDialog(entityLoop.detail.systemTypeCode, unitData).subscribe({
      next: (input: SureFlowUnitData) => {
        if (input) {
          this.isBusy.set(true);
          this.errorMessage.set(undefined!);
          const ratingInput = new SureFlowEntityRatingInput();
          ratingInput.loop = entityLoop;
          ratingInput.units = [];
          const updatedUnitData: SureFlowUnitData = JSON.parse(JSON.stringify(input)) as SureFlowUnitData;
          const entityUnit: EntitySureFlowUnit = new EntitySureFlowUnit();
          entityUnit.unitData = updatedUnitData;
          entityUnit.loopId = entityLoop.id;
          entityUnit.sequence = this.getNextUnitSequence();
          const unitRatingInput = new SureFlowEntityUnitRatingInput();
          unitRatingInput.unit = entityUnit;
          ratingInput.units.push(unitRatingInput);
          this.unitService.saveEntityLoopUnits(ratingInput).subscribe({
            next: () => {
              this.loadLoopList().subscribe({
                next: () => {
                  this.changeEntityLoopId(entityLoop.id, entityLoop.detail.id).subscribe({
                    next: () => this.isBusy.set(false),
                    error: (error) => this.handleError(error)
                  });
                }, error: (error) => this.handleError(error),
              });
            }, error: (error) => this.handleError(error),
          });
        }
      }, error: (error) => this.handleError(error),
    });
  }

  private getNextUnitSequence(): number {
    if ((this.loopUnits()?.length || 0) === 0) {
      return 1;
    } else {
      let maxSequence: number = 0;
      for (const unit of this.loopUnits()) {
        if (maxSequence < unit.sequence) {
          maxSequence = unit.sequence;
        }
      }
      return maxSequence + 1;
    }
  }

  rearrangeUnits(detail: SureFlowLoopDetail): void {
    this.errorMessage.set(undefined!);
    this.loopUnits.update(current => [...current].sort((first, second) => first.sequence - second.sequence));
    const unitDetails: SureFlowUnitData[] = this.loopUnits().map(unit => unit.unitData);
    this.dialogService.showRearrangeSureFlowUnitsDialog(unitDetails).subscribe({
      next: (rearranged) => {
        if ((rearranged?.length || 0) > 0) {
          this.isBusy.set(true);
          const entityLoop: EntitySureFlowLoop = this.getEntityLoop(detail.id);
          const ratingInputs: SureFlowEntityUnitRatingInput[] = this.buildEntityUnitList(rearranged);
          const input: SureFlowEntityRatingInput = new SureFlowEntityRatingInput();
          input.units = ratingInputs;
          input.loop = entityLoop;
          this.sureFlowRatingService.rearrangeEntityUnits(input).subscribe({
            next: () => {
              this.loadLoopList().subscribe({
                next: () => {
                  this.changeEntityLoopId(entityLoop.id, detail.id).subscribe({
                    next: () => this.isBusy.set(false),
                    error: (error) => this.handleError(error)
                  });
                }, error: (error) => this.handleError(error),
              });
            }, error: (error) => this.handleError(error)
          });
        }
      }, error: (error) => this.handleError(error)
    });
  }

  private buildEntityUnitList(rearranged: SureFlowUnitData[]): SureFlowEntityUnitRatingInput[] {
    this.loopUnits.update(current => [...current].sort((first, second) => first.sequence - second.sequence));
    const sequenceList: number[] = this.loopUnits().map(unit => unit.sequence);
    const inputs: SureFlowEntityUnitRatingInput[] = [];
    for (let index = 0; index < rearranged.length; index++) {
      const currentData: SureFlowUnitData = rearranged[index];
      const unit: EntitySureFlowUnit = this.loopUnits().filter(loopUnit => loopUnit.unitData.id === currentData.id)[0];
      unit.sequence = sequenceList[index];
      const input = new SureFlowEntityUnitRatingInput();
      input.unit = unit;
      inputs.push(input);
    }
    return inputs;
  }

  unitSelectionChanged(units: WorkingSureFlowUnit[]): void {
    if ((units?.length || 0) > 0) {
      this.selectedUnitIds.set(units.map(unit => unit.id));
    } else {
      this.selectedUnitIds.set([]);
    }
  }


  private initialize(): void {
    if (isNaN(this.entityRevision()) || StringUtils.isBlank(this.entityId()) || this.isUnknownEntity(this.entityName())) {
      this.backToEntity();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }
}
